/* eslint-disable no-console */
import {Col, Form, Input, Select, Row} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React from 'react';

import {
  FacilityHeadquarterLabel,
  FacilityKey,
  FacilityLabel
} from '../../../../components/Forms/Constants/IP/FacilityKeyLabel';
import {filterSelectOptions} from '../../../../components/Forms/Utils/commonFunctions';
import {facilityTypeIds} from '../../../../components/Table/Constants/Common';
import {CommonErrors, transformInput} from '../../../../constants/Common';
import useJSONStore from '../../../../store/StaticJson/jsonStore';
import {
  FirstCap,
  eachWordCapOrAllCap
} from '../../../../utils/FormUtils/FirstCap';
import RegEx from '../../../../utils/FormUtils/RegEx';

const FacilityHeadquarterForm: React.FC<{
  data?: any;
  setSelectedCountry: React.Dispatch<React.SetStateAction<string>>;
}> = ({data = undefined, setSelectedCountry}) => {
  const countryOptions = useJSONStore((state) => state.country);
  const stateOptions = useJSONStore((state) => state.states);
  const cityOptions = useJSONStore((state) => state.city);

  const FormInstance = useFormInstance();
  const {setFields, getFieldValue} = FormInstance;

  return (
    <>
      <Col className="gutter-row">
        <Form.Item
          name={['facilities', FacilityKey.FACILITY_NAME]}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(
                FacilityHeadquarterLabel.HEADQUARTER_NAME
              )
            },
            {
              max: 100,
              message: `${FacilityHeadquarterLabel.HEADQUARTER_NAME} should be less than 100 characters`
            },
            {
              min: 3,
              message: `${FacilityHeadquarterLabel.HEADQUARTER_NAME} should be more than 2 characters`
            },
            {
              pattern: RegEx.businessName,
              message: CommonErrors.noAllowedSpecChar
            }
          ]}
          label={FacilityHeadquarterLabel.HEADQUARTER_NAME}
          labelCol={{span: 24}}
          initialValue={''}
          normalize={(value) => transformInput.removeExtraSpace(value)}
          validateFirst
          hasFeedback
        >
          <Input
            className="input"
            placeholder={`Enter ${FacilityHeadquarterLabel.HEADQUARTER_NAME}`}
            onInput={(e) => {
              const target = e.target as HTMLInputElement;
              target.value = eachWordCapOrAllCap(target.value);
            }}
          />
        </Form.Item>
      </Col>

      <Col className="gutter-row">
        <Form.Item
          name={['facilities', FacilityKey.ADDRESS]}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(
                FacilityHeadquarterLabel.HEADQUARTER_ADDRESS
              )
            },
            {
              whitespace: true,
              message: CommonErrors.mandatory(
                FacilityHeadquarterLabel.HEADQUARTER_ADDRESS
              )
            }
          ]}
          label={FacilityHeadquarterLabel.HEADQUARTER_ADDRESS}
          labelCol={{span: 24}}
          initialValue={''}
          normalize={(value) => transformInput.removeExtraSpace(value)}
          validateFirst
          hasFeedback
        >
          <Input
            className="input"
            placeholder={`Enter ${FacilityHeadquarterLabel.HEADQUARTER_ADDRESS}`}
            onInput={(e) => {
              const target = e.target as HTMLInputElement;
              target.value = FirstCap(target.value);
            }}
          />
        </Form.Item>
      </Col>

      <Row gutter={16}>
        <Col xs={24} sm={12} md={8} lg={8}>
          <Form.Item
            name={['facilities', FacilityKey.COUNTRY]}
            label={FacilityLabel.COUNTRY}
            labelCol={{span: 24}}
            rules={[
              {
                required: true,
                message: CommonErrors.mandatory(FacilityLabel.COUNTRY)
              }
            ]}
            validateFirst
            hasFeedback
            // shouldUpdate
          >
            <Select
              showSearch
              className="select"
              placeholder={`Choose ${FacilityLabel.COUNTRY}`}
              filterOption={filterSelectOptions}
              options={countryOptions}
              onChange={(e) => {
                if (
                  getFieldValue(['facilities', FacilityKey.COUNTRY]) &&
                  getFieldValue(['facilities', FacilityKey.STATE])
                ) {
                  setFields([
                    {
                      name: ['facilities', FacilityKey.STATE],
                      errors: ['Please Select State again'],
                      value: undefined
                    },
                    {
                      name: ['facilities', FacilityKey.CITY],
                      errors: ['Please Select City again'],
                      value: undefined
                    }
                  ]);
                }
                setSelectedCountry(e);
              }}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8} lg={8}>
          <Form.Item shouldUpdate noStyle>
            {({getFieldValue}) => (
              <Form.Item
                name={['facilities', FacilityKey.STATE]}
                label={FacilityLabel.STATE}
                labelCol={{span: 24}}
                // style={{marginBottom: '0px'}}
                rules={[
                  {
                    required: true,
                    message: CommonErrors.mandatory(FacilityLabel.STATE)
                  }
                ]}
                validateFirst
                hasFeedback
              >
                <Select
                  showSearch
                  className="select"
                  placeholder={`Choose ${FacilityLabel.STATE}`}
                  disabled={!getFieldValue(['facilities', FacilityKey.COUNTRY])}
                  filterOption={filterSelectOptions}
                  value={data?.facilities?.[FacilityKey.STATE] ?? undefined}
                  onChange={() => {
                    if (getFieldValue(['facilities', FacilityKey.CITY])) {
                      setFields([
                        {
                          name: ['facilities', FacilityKey.CITY],
                          errors: ['Please Select City again'],
                          value: undefined
                        }
                      ]);
                    }
                  }}
                  options={
                    getFieldValue(['facilities', FacilityKey.COUNTRY])
                      ? stateOptions.filter(
                          (item) =>
                            item?.country_id ===
                            getFieldValue(['facilities', FacilityKey.COUNTRY])
                        )
                      : [{value: 0, label: ''}]
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={8}>
          <Form.Item shouldUpdate noStyle>
            {({getFieldValue}) => (
              <Form.Item
                name={['facilities', FacilityKey.CITY]}
                label={FacilityLabel.CITY}
                labelCol={{span: 24}}
                // style={{marginBottom: '0px'}}
                rules={[
                  {
                    required: true,
                    message: CommonErrors.mandatory(FacilityLabel.CITY)
                  }
                ]}
                validateFirst
                hasFeedback
              >
                <Select
                  showSearch
                  className="select"
                  placeholder={`Choose ${FacilityLabel.CITY}`}
                  disabled={
                    !(
                      getFieldValue(['facilities', FacilityKey.STATE]) &&
                      getFieldValue(['facilities', FacilityKey.COUNTRY])
                    )
                  }
                  value={
                    cityOptions?.length > 0
                      ? data?.[FacilityLabel.CITY]
                      : undefined
                  }
                  filterOption={filterSelectOptions}
                  options={
                    getFieldValue(['facilities', FacilityKey.STATE])
                      ? cityOptions?.filter(
                          (item) =>
                            item?.state_id ===
                            getFieldValue(['facilities', FacilityKey.STATE])
                        )
                      : [{value: 0, label: ''}]
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        name={['facilities', FacilityKey.FACILITY_TYPE]}
        initialValue={facilityTypeIds.headquarter}
        hidden
      >
        <Input />
      </Form.Item>
    </>
  );
};

export default FacilityHeadquarterForm;
